import React, { useState, useEffect, useMemo } from 'react';
import { Form, Checkbox, Message, Label } from 'semantic-ui-react';
import styled from 'styled-components';
import { StepComponentProps } from './OrganizationWizard';
import { categoryApi } from '../../category/api/categoryApi';
import { organizationApi } from '../api/organizationApi';
import { v4 as uuidv4 } from 'uuid';
import LoadingSpinner from 'common/components/LoadingSpinner';
import { useToasts } from 'common/toast';

const OrganizationWizardStep3Categories: React.FC<StepComponentProps> = ({
  data,
  onDataChange,
  onNext,
  onBack,
  onCancel,
}) => {
  const { addToast } = useToasts();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  const { data: allCategories = [], isLoading } = categoryApi.useApiAdminV1CategoryListQuery({
    status: 1,
  });

  // Load the current organization so we can append categories without overwriting
  const { data: org } = organizationApi.useApiAdminV1OrganizationRetrieveQuery(
    { id: data.organizationId ?? -1 },
    { skip: !data.organizationId }
  );

  const [updateOrganization] = organizationApi.useApiAdminV1OrganizationPartialUpdateMutation();

  // Combine existing categories with newly created ones
  const availableCategories = useMemo(() => {
    if (!data.organizationId) return [];

    // Build a set of saved IDs for categories created in Step 2 so we can avoid showing
    // them twice (once as a local 'newCategory' and once as an API-returned category).
    const savedCategoryIdSet = new Set<number>(
      (data.newCategories || [])
        .map((nc: any) => (nc.savedId != null ? Number(nc.savedId) : undefined))
        .filter((v): v is number => v !== undefined)
    );

    // Get existing categories from API, but exclude any that were just created in Step 2
    const existingCategories = allCategories
      .filter(
        cat =>
          (Number(cat.priority) === 3 ||
            ((Number(cat.priority) === 1 || Number(cat.priority) === 2) &&
              cat.organization === data.organizationId)) &&
          !savedCategoryIdSet.has(Number(cat.id))
      );

    // Convert new categories from step 2 to the same format
    const newCategories = (data.newCategories || []).map((newCat: any) => ({
      id: newCat.id,
      name: newCat.name,
      priority: newCat.isPrimary ? 1 : 2, // Primary (1) or Custom (2)
      organization: data.organizationId,
      default_visibility: newCat.default_visibility,
      default_prompt: newCat.default_prompt,
      default_question: newCat.default_question,
      categories: newCat.options,
    }));

    // Combine and sort
    return [...newCategories, ...existingCategories].sort((a, b) => {
      // Sort by priority first, then by name
      if (Number(a.priority) !== Number(b.priority)) {
        return Number(a.priority) - Number(b.priority);
      }
      return (a.name ?? '').localeCompare(b.name ?? '');
    });
  }, [allCategories, data.organizationId, data.newCategories]);

  // Initialize selection from wizard data OR pre-select newly created categories
  useEffect(() => {
    if (data.selectedCategories.length > 0) {
      const ids = data.selectedCategories.map(c => String(c.categoryId));
      setSelectedIds(new Set(ids));
    } else if (data.newCategories && data.newCategories.length > 0) {
      // Pre-select newly created categories
      const newCategoryIds = data.newCategories.map((c: any) => String(c.id));
      setSelectedIds(new Set(newCategoryIds));
    }
  }, [data.selectedCategories, data.newCategories]);

  const toggleCategory = (categoryId: string) => {
    // Check if this is a primary category (priority 1)
    const category = availableCategories.find(c => String(c.id) === categoryId);
    const isPrimary = category && Number(category.priority) === 1;
    
    // Don't allow toggling primary category
    if (isPrimary) {
      return;
    }
    
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
    setErrors('');
  };

  const toggleAll = () => {
    if (selectedIds.size === availableCategories.length) {
      // When deselecting all, keep primary categories selected
      const primaryIds = availableCategories
        .filter(c => Number(c.priority) === 1)
        .map(c => String(c.id));
      setSelectedIds(new Set(primaryIds));
    } else {
      setSelectedIds(new Set(availableCategories.map(c => String(c.id))));
    }
    setErrors('');
  };

  const handleContinue = async () => {
    if (selectedIds.size === 0) {
      setErrors('Please select at least one category');
      return;
    }

    // Convert selected categories to the format needed for next step
    const selected = availableCategories
      .filter(c => selectedIds.has(String(c.id)))
      .map(c => ({
        categoryId: c.id,
        id: uuidv4(),
        name: c.name,
        input: c.default_prompt,
        categories: c.categories || [],
        options: c.categories?.map((cat: any) => ({
          id: String(cat.id),
          name: cat.name,
          color: cat.color,
          description: cat.description,
          fr: cat.fr,
        })) || [],
        priority: c.priority,
        subtitle: c.name,
        visibility: c.default_visibility,
        question: c.default_question,
        subfactors: [],
      }));

    // Persist selected categories to the organization using the same shape used elsewhere
    // (OrganizationSurveyStructureAddCategories creates local org category objects with uuid ids)
    if (data.organizationId) {
      const existingNames = new Set((org?.categories || []).map((c: any) => (c.name || '').toLowerCase()));

      const toAdd = selected
        .filter((s: any) => !existingNames.has((s.name || '').toLowerCase()))
        .map((s: any) => ({
          id: uuidv4(),
          name: s.name,
          input: s.input,
          options: s.options || [],
          priority: s.priority === 1 ? 'primary' : s.priority === 2 ? 'secondary' : 'tertiary',
          subtitle: s.name,
          visibility: s.visibility || 'private',
          question: s.question || '',
          subfactors: [],
        }));

      // Determine removals: only remove categories that were previously selected in the wizard
      const prevSelectedNames = new Set((data.selectedCategories || []).map((c: any) => (c.name || '').toLowerCase()));
      const currentSelectedNames = new Set(selected.map((s: any) => (s.name || '').toLowerCase()));

      const toRemoveNames = Array.from(prevSelectedNames).filter(n => !currentSelectedNames.has(n));

      const toRemoveSet = new Set(toRemoveNames);

      if (toAdd.length > 0 || toRemoveNames.length > 0) {
        setIsSaving(true);
        try {
          // Build updated categories: start with org categories, remove those that are in toRemoveSet,
          // then append toAdd entries.
          const filtered = (org?.categories || []).filter((c: any) => !toRemoveSet.has((c.name || '').toLowerCase()));
          const updated = [...filtered, ...toAdd];

          await updateOrganization({ id: data.organizationId, patchedOrganization: { categories: updated } as any }).unwrap();

          if (toAdd.length > 0) {
            addToast(`${toAdd.length} ${toAdd.length === 1 ? 'category' : 'categories'} added`, { appearance: 'success' });
          }

          if (toRemoveNames.length > 0) {
            addToast(`${toRemoveNames.length} ${toRemoveNames.length === 1 ? 'category' : 'categories'} removed`, { appearance: 'success' });
          }
        } catch (err) {
          console.error('Failed to save selected categories to organization:', err);
          addToast('Failed to save categories', { appearance: 'error' });
        } finally {
          setIsSaving(false);
        }
      }
    }

    onDataChange({ selectedCategories: selected });
    onNext();
  };

  const getPriorityLabel = (priority: number) => {
    switch (priority) {
      case 1:
        return 'Primary';
      case 2:
        return 'Custom';
      case 3:
        return 'Global';
      default:
        return 'Other';
    }
  };

  const getPriorityColor = (priority: number): 'blue' | 'orange' | 'purple' | 'grey' => {
    switch (priority) {
      case 1:
        return 'blue';
      case 2:
        return 'orange';
      case 3:
        return 'purple';
      default:
        return 'grey';
    }
  };

  if (!data.organizationId) {
    return (
      <StepContainer>
        <Message warning>
          <Message.Header>Organization Not Created</Message.Header>
          <p>Please complete Step 1 first to create the organization.</p>
        </Message>
        <ButtonContainer>
          <LeftButtons>
            <BackButton onClick={onBack}>Back to Step 1</BackButton>
            <CancelButton onClick={onCancel}>Cancel Setup</CancelButton>
          </LeftButtons>
        </ButtonContainer>
      </StepContainer>
    );
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const allSelected = selectedIds.size === availableCategories.length && availableCategories.length > 0;
  const someSelected = selectedIds.size > 0;

  return (
    <StepContainer>
      <StepHeader>
        <StepTitle>Select Demographic Categories</StepTitle>
        <StepSubtitle>
          Choose the member categories you want to track for this organization.
        </StepSubtitle>
      </StepHeader>

      {errors && <Message negative>{errors}</Message>}

      <SelectionHeader>
        <Checkbox
          label={allSelected ? 'Deselect All' : 'Select All'}
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          onChange={toggleAll}
        />
        <SelectionCount>
          {selectedIds.size} of {availableCategories.length} selected
        </SelectionCount>
      </SelectionHeader>

      <CategoriesGrid>
        {availableCategories.map(category => {
          const isSelected = selectedIds.has(String(category.id));
          const isPrimary = Number(category.priority) === 1;
          return (
            <CategoryCard
              key={category.id}
              $selected={isSelected}
              $disabled={isPrimary}
              onClick={() => toggleCategory(String(category.id))}
            >
              <CategoryContent>
                <Checkbox checked={isSelected} disabled={isPrimary} onChange={() => {}} />
                <CategoryName>{category.name}</CategoryName>
                <Label size="tiny" color={getPriorityColor(category.priority ?? 3)}>
                  {getPriorityLabel(category.priority ?? 3)}
                </Label>
              </CategoryContent>
            </CategoryCard>
          );
        })}
      </CategoriesGrid>

      {availableCategories.length === 0 && (
        <Message info>
          <Message.Header>No Categories Available</Message.Header>
          <p>There are no categories available for selection. You can create custom categories later.</p>
        </Message>
      )}

      <ButtonContainer>
        <LeftButtons>
          <BackButton onClick={onBack}>Back</BackButton>
          <CancelButton onClick={onCancel}>Cancel Setup</CancelButton>
        </LeftButtons>
        <NextButton onClick={handleContinue} disabled={selectedIds.size === 0 || isSaving}>
          {isSaving ? 'Saving...' : 'Continue'}
        </NextButton>
      </ButtonContainer>
    </StepContainer>
  );
};

export default OrganizationWizardStep3Categories;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
    padding-right: 32px;

`;

const StepHeader = styled.div`
  margin-bottom: 20px;
`;

const StepTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
`;

const StepSubtitle = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0;
  line-height: 1.4;
`;

const SelectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 4px;
`;

const SelectionCount = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: 500;
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
`;

const CategoryCard = styled.div<{ $selected: boolean; $disabled?: boolean }>`
  padding: 10px 12px;
  border: 2px solid ${props => (props.$selected ? '#2185d0' : '#e0e0e0')};
  border-radius: 6px;
  background: ${props => (props.$selected ? '#f0f8ff' : '#fff')};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #2185d0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const CategoryContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CategoryName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CategoryStats = styled.div`
  font-size: 11px;
  color: #666;
  white-space: nowrap;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
`;

const BackButton = styled.button`
  padding: 10px 24px;
  background: transparent;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #f5f5f5;
    border-color: #b0b0b0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LeftButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const CancelButton = styled.button`
  padding: 10px 24px;
  background: transparent;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #b0b0b0;
  }
`;

const NextButton = styled.button`
  padding: 10px 24px;
  background: #2185d0;
  border: none;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #1678c2;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
